import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ArrowRight, ShieldCheck, UserCheck, Layers, CheckCheck, Users, Zap } from "lucide-react";

const accounting = [
  "Bookkeeping & Ledger Management",
  "Accounts Payable (AP)",
  "Accounts Receivable (AR)",
  "Inventory & Cost Tracking",
  "Payroll Processing",
  "Financial Planning & Analysis (FP&A)",
  "Sales Tax Compliance",
  "Management Reporting",
  "Xero Bookkeeping",
  "QuickBooks Bookkeeping",
];

const taxation = [
  "Outsourced Tax Support",
  "Individual Returns (1040)",
  "Partnership & LLC/LLP Returns",
  "C-Corporation Returns",
  "S-Corporation Returns",
  "Trust & Estate Returns",
  "Tax Planning & Advisory",
];

const whyUs = [
  {
    icon: ShieldCheck,
    title: "Security-First by Design",
    desc: "Your financial information is protected through secure systems, controlled access, and enterprise-grade confidentiality — ensuring your data remains safe at every stage.",
  },
  {
    icon: UserCheck,
    title: "Seasoned Finance Experts",
    desc: "Our team comprises experienced accounting and tax professionals with the technical expertise to manage complex financial operations with precision and consistency.",
  },
  {
    icon: Layers,
    title: "Industry-Specific Financial Expertise",
    desc: "We understand the unique accounting, bookkeeping, and tax requirements across industries including SaaS, professional services, healthcare, e-commerce, real estate, manufacturing, and retail.",
  },
  {
    icon: CheckCheck,
    title: "Dual-Layer Quality Assurance",
    desc: "Every engagement follows a rigorous Maker–Checker review process, where every deliverable is independently verified before it reaches you — ensuring superior accuracy, compliance, and reliability.",
  },
  {
    icon: Users,
    title: "Your Extended Finance Team",
    desc: "We seamlessly integrate with your business as an extension of your finance function, providing proactive support, dependable communication, and long-term partnership — not just outsourced services.",
  },
  {
    icon: Zap,
    title: "Responsive. Reliable. Accountable.",
    desc: "Dedicated points of contact, quick turnaround times, and proactive communication ensure your questions are answered promptly and your deadlines are never missed.",
  },
];

const ListBlock = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-display text-2xl md:text-3xl text-[#0A0A0A] mb-6">{title}</h3>
    <ul className="border-t border-[#E5E5E5]">
      {items.map((it) => (
        <li key={it} className="group border-b border-[#E5E5E5]">
          <div className="py-3.5 text-[#52525B] group-hover:text-[#3B37E5] transition-colors">
            <span className="text-lg">{it}</span>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

export default function ServicesPage() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="services-page" className="relative bg-white">
      <Navbar />

      {/* intro */}
      <section className="pt-[88px] bg-gradient-to-br from-[#F7F8FA] to-[#ECEEF2]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="label-tech !text-[#3B37E5] mb-6"
          >
            Offshore accounting &amp; tax preparation
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-[2.75rem] sm:text-6xl lg:text-[4.25rem] leading-[0.97] text-[#0A0A0A] max-w-[20ch]"
          >
            A full finance function, delivered offshore.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 text-lg text-[#52525B] leading-relaxed max-w-2xl"
          >
            From day-to-day bookkeeping to complex, multi-entity tax returns, our teams plug into your business and run the numbers with precision, security, and complete confidentiality.
          </motion.p>
        </div>
      </section>

      {/* service catalog */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="label-tech !text-[#3B37E5] mb-3">Accounting Services</p>
            <span className="block mb-8 h-[3px] w-16 bg-gradient-to-r from-[#14213D] to-[#3B37E5] rounded-full" />
            <ListBlock title="What we handle" items={accounting} testid="accounting-list" />
          </div>
          <div className="space-y-14">
            <div>
              <p className="label-tech !text-[#3B37E5] mb-3">Tax Preparation Services</p>
              <span className="block mb-8 h-[3px] w-16 bg-gradient-to-r from-[#14213D] to-[#3B37E5] rounded-full" />
              <ListBlock title="Returns &amp; planning" items={taxation} testid="tax-list" />
            </div>
          </div>
        </div>
      </section>

      {/* why us */}
      <section id="why-us" data-testid="why-us-section" className="bg-[#F3F2EF] py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="label-tech !text-[#3B37E5] mb-6">Why Us</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.98] text-[#0A0A0A] max-w-[18ch] mb-16">
            Built to be trusted with your numbers.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.title}
                  data-testid={`why-us-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                  className="bg-white rounded-lg p-8 border border-[#EAECF0] shadow-[0_8px_24px_rgba(20,33,61,0.05)]"
                >
                  <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#14213D] mb-6">
                    <Icon size={20} className="text-white" strokeWidth={2} />
                  </span>
                  <h3 className="font-display text-xl text-[#0A0A0A] mb-3 leading-tight">{w.title}</h3>
                  <p className="text-[#52525B] leading-relaxed">{w.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#14213D] text-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl leading-[0.98] max-w-[16ch]">
            Ready to hand off the books and taxes?
          </h2>
          <button
            data-testid="services-cta"
            onClick={() => navigate("/#contact")}
            className="group inline-flex items-center gap-2 label-tech !text-white border-b border-white/40 pb-2 hover:!text-[#9CA3F5] hover:border-[#9CA3F5] transition-colors self-start lg:self-auto"
          >
            Start a conversation
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
